import * as Sentry from '@sentry/react';
import flatMap from 'lodash/flatMap';
import isArray from 'lodash/isArray';
import { Integrations } from '@sentry/tracing';
import { envConfig } from '../../utils/constants';
import { formatGlobalVariantInGlobalProduct } from './BundleUtils';
import {
  FEATURE_FLAGS,
  isFeatureFlagEnabled,
} from '../../widgetSrc/utils/featureFlags';

export const CURRENCY_STYLE = 'currency';
export const PERCENTAGE_PARAM_TYPE = 'p';
export const AMOUNT_PARAM_TYPE = 'q';
export const PERCENTAGE_TYPE = 'percentage';
export const AMOUNT_TYPE = 'fixed_amount';
export const PRICE_TYPE = 'price';
export const BAB_SENTRY_APP_NAME = 'bundles';
export const CP_SENTRY_APP_NAME = 'subscriptions';
export const SENTRY_APP_DSN_MAP = {
  [CP_SENTRY_APP_NAME]:
    'https://ae91fb3fcf8c47109fc665e2faccb95f@o546951.ingest.sentry.io/5812322',
  [BAB_SENTRY_APP_NAME]:
    'https://4ed2a787fd5c4aa0bf56da68135720da@o546951.ingest.sentry.io/4504763686322176',
};

const getUserLocale = (): string =>
  Intl.DateTimeFormat().resolvedOptions().locale;

export const getCurrencyFormatter = (
  currencyCode?: string,
  minimumFractionDigits = 2,
  locale?: string,
): ((amount: number | string) => string) => {
  const userLocale = locale ?? getUserLocale();

  // In case Intl not supported (IE<11)
  if (!Intl.NumberFormat || !currencyCode) {
    return (amount: number) => `${amount}${currencyCode}`;
  }
  const { format } = Intl.NumberFormat(userLocale, {
    style: CURRENCY_STYLE,
    currency: currencyCode,
    minimumFractionDigits,
  });

  return format;
};

export const listFormatter = (
  { type, style, locale }: { type: any; style: any; locale?: string },
  list: string[],
): string => {
  if (!Intl.ListFormat) {
    return list.join(', ');
  }

  const userLocale = locale ?? getUserLocale();
  return new Intl.ListFormat(userLocale, { type, style }).format(list);
};

export const isGid = (id: string): boolean => {
  return id.startsWith('gid://');
};

export const getIdFromGid = (gid = ''): string | undefined => {
  if (gid.indexOf('gid') === -1) {
    return '';
  }
  return gid.split('/').pop();
};

export const getGidFromIdForResource = (
  id: string,
  resource: string,
): string => {
  if (!id || !resource) {
    throw new Error('The meethod needs an ID and a valid resource');
  }
  return `gid://shopify/${resource}/${id}`;
};

export const initializeSentry = ({ appName }: { appName: string }): void => {
  if (process.env.BUNDLE_ENV !== 'dev') {
    try {
      Sentry.init({
        dsn: SENTRY_APP_DSN_MAP[appName],
        integrations: [
          new Integrations.BrowserTracing(),
          new Sentry.Replay({
            blockAllMedia: false,
          }),
        ],
        environment: envConfig[process.env.BUNDLE_ENV].env,
        tracesSampleRate: envConfig[process.env.BUNDLE_ENV].tracesSampleRate,
        replaysOnErrorSampleRate:
          envConfig[process.env.BUNDLE_ENV].replaysOnErrorSampleRate,
        ignoreErrors: [
          //not tested
          'checkNovum',
          '<unknown>',
          // Random plugins/extensions
          'top.GLOBALS',
          // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
          'originalCreateNotification',
          'canvas.contentDocument',
          'MyApp_RemoveAllHighlights',
          'http://tt.epicplay.com',
          "Can't find variable: ZiteReader",
          'jigsaw is not defined',
          'ComboSearch is not defined',
          'http://loading.retry.widdit.com/',
          'atomicFindClose',
          // Facebook borked
          'fb_xd_fragment',
          // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
          // reduce this. (thanks @acdha)
          // See http://stackoverflow.com/questions/4113268
          'bmi_SafeAddOnload',
          'EBCallBackMessageReceived',
          // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
          'conduitPage',
        ],

        beforeSend: (event) => {
          const jsAppNames = [
            'awtomic-customerportal.js',
            'awtomic-bundles.js',
            'awtomic-bundlesThemed.js',
          ];
          if (event?.exception && event?.exception?.values) {
            const frames =
              event?.exception?.values[0]?.stacktrace?.frames || [];
            const isAwtomicError = frames?.some(
              (frame) =>
                frame?.filename &&
                jsAppNames.some(
                  (appName) => frame?.filename?.includes(appName),
                ),
            );
            if (isAwtomicError) {
              return event;
            }
          }

          return null;
        },

        denyUrls: [
          '<anonymous>',
          /.*azexo\.js.*/,
          /.*privy\.com\/widget\.js.*/,
          /.*ellipsis\.js.*/,
          /.*shopifyChatV1\.js.*/,
          /.*facebook-chat\.js.*/,
          // Facebook flakiness
          /graph\.facebook\.com/i,
          // Facebook blocked
          /connect\.facebook\.net\/en_US\/all\.js/i,
          // Woopra flakiness
          /eatdifferent\.com\.woopra-ns\.com/i,
          /static\.woopra\.com\/js\/woopra\.js/i,
          // Chrome extensions
          /extensions\//i,
          /^chrome:\/\//i,
          // Other plugins
          /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
          /webappstoolbarba\.texthelp\.com\//i,
          /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        ],
        initialScope: {
          tags: { appName, awtomicApp: true },
        },
      });
    } catch (error) {
      console.error('Error loading Sentry', error);
    }
  }
};

export const getDiscountQuantityFromParam = (
  discountParam: string,
  price: number,
): number => {
  let discount = 0;

  if (discountParam) {
    try {
      const [quantity, type] = discountParam.split('-');
      if (type === PERCENTAGE_PARAM_TYPE) {
        discount = price * (parseFloat(quantity) / 100);
      } else if (type === AMOUNT_PARAM_TYPE) {
        discount =
          (parseFloat(quantity) / 100) * (window?.Shopify?.currency?.rate || 1);
      }
    } catch (e) {
      console.error('wrong discount format');
    }
  }

  return discount;
};

export const getDiscountFromSellingPriceAdjustment = (
  price: number,
  sellingPriceAdjustment?: SellingPlan['price_adjustments'],
):
  | number
  | {
      type: string;
      value: number;
    } => {
  let discount = 0;

  if (sellingPriceAdjustment?.length) {
    try {
      const { value: quantity, value_type: type } = sellingPriceAdjustment[0];

      if (type === PERCENTAGE_TYPE) {
        discount = price * (quantity / 100);
      } else if (type === AMOUNT_TYPE) {
        discount = (quantity / 100) * (window?.Shopify?.currency?.rate || 1);
      } else if (type === PRICE_TYPE) {
        return { type: PRICE_TYPE, value: quantity };
      }
    } catch (e) {
      console.error('wrong discount format');
    }
  }

  return discount;
};

export const getDiscountDataFromSellingPlan = (
  product: { selling_plan_groups: SellingPlanGroup[] } | LiquidProduct,
  sellingPlan: string | number,
): SellingPlan['price_adjustments'] => {
  const productSellingPlans = flatMap(
    product.selling_plan_groups,
    'selling_plans',
  );

  const sellingPlanData = productSellingPlans?.find(
    ({ id }) => `${id}` === `${sellingPlan}`,
  );
  return sellingPlanData?.price_adjustments || [];
};

const getSellingPlanFromAddonPlanAllocations = (addon, sellingPlanId) =>
  addon.sellingPlanAllocations?.find(
    (spa) => spa.selling_plan_id?.toString() === sellingPlanId,
  );

export const getSellingPlanIdFromURL = () => {
  const urlParams = new URLSearchParams(window?.location.search);

  return urlParams.get('selling_plan') || '';
};

export const getDiscountTypeFromURL = () => {
  const urlParams = new URLSearchParams(window?.location.search);
  const discountParam = urlParams.get('d') || '';
  const [, discountType] = discountParam.split('-');

  return discountType;
};

export const getDiscountedPriceURLDiscount = (price: number): number => {
  const urlParams = new URLSearchParams(window?.location.search);
  const discountParam = urlParams.get('d') || '';
  const discount = getDiscountQuantityFromParam(discountParam, price);

  return price - discount;
};

export const getAddonPrice = (addon) => {
  const sellingPlanParam = getSellingPlanIdFromURL();
  const discountType = getDiscountTypeFromURL();

  const sellingPlan = getSellingPlanFromAddonPlanAllocations(
    addon,
    sellingPlanParam,
  );

  if (discountType !== PERCENTAGE_PARAM_TYPE) {
    return addon.price;
  }

  return sellingPlan ? sellingPlan.price / 100 : addon.price;
};

export const getAddonCurrentPrice = (addon) => {
  const sellingPlanParam = getSellingPlanIdFromURL();
  const discountType = getDiscountTypeFromURL();

  const sellingPlan = getSellingPlanFromAddonPlanAllocations(
    addon,
    sellingPlanParam,
  );

  if (discountType !== PERCENTAGE_PARAM_TYPE) {
    return undefined;
  }

  return sellingPlan ? sellingPlan.compare_at_price / 100 : undefined;
};

export const getProductPrice = (
  product,
  discount?: {
    type: string;
    value: number;
  },
): number | undefined => {
  if (discount) {
    const price = Number(product.price);
    const discountedPrice = calculateDiscount(price, discount);
    return Number((price - discountedPrice).toPrecision(3));
  }
  const sellingPlanParam = getSellingPlanIdFromURL();
  const discountType = getDiscountTypeFromURL();

  const sellingPlan = getSellingPlanFromAddonPlanAllocations(
    product,
    sellingPlanParam,
  );

  if (!discount || !sellingPlan) return;

  if (discountType !== PERCENTAGE_PARAM_TYPE) {
    return product.price;
  }

  return sellingPlan ? sellingPlan.price / 100 : product.price;
};

export const calculateDiscount = (
  price: number,
  discount?: {
    type: string;
    value: number;
  },
): number => {
  let discountedPrice = 0;

  if (discount) {
    const { value: quantity, type } = discount;
    if (type.toLowerCase() === PERCENTAGE_TYPE) {
      discountedPrice = Number(price * (quantity / 100));
    } else if (type.toLowerCase() === AMOUNT_TYPE) {
      discountedPrice = quantity;
    }
  }

  return discountedPrice;
};

export const getProductCurrentPrice = (
  product?: GlobalVariant | GlobalProduct,
  discount?: {
    type: string;
    value: number;
  },
): number | undefined => {
  if (!product) return;
  const price = Number(product.price);

  if (discount) {
    const discountedPrice = calculateDiscount(price, discount);
    return Number((price - discountedPrice).toPrecision(3));
  }

  const sellingPlanParam = getSellingPlanIdFromURL();

  const sellingPlan = getSellingPlanFromAddonPlanAllocations(
    product,
    sellingPlanParam,
  );

  return sellingPlan?.compare_at_price
    ? sellingPlan.compare_at_price / 100
    : price;
};

export const getPriceFromSellingPlanAllocations = (
  product: GlobalVariant | GlobalProduct,
  sellingPlanId?: string | number,
): number | undefined => {
  if (isGlobalProduct(product)) {
    return;
  }

  if (!sellingPlanId) return;

  const productPrices = product?.sellingPlanAllocations?.find(
    (sp) => sp.selling_plan_id.toString() === sellingPlanId.toString(),
  );

  if (productPrices?.price) return productPrices.price / 100;

  return;
};

// Created a custom function to replace the lodash _.uniqBy because the lodash
// version is filtering elements with the key as undefined.
export const uniqBy = <T>(
  arr: T[],
  predicate: ((item: unknown) => string) | string,
): T[] => {
  if (!Array.isArray(arr)) {
    return [];
  }

  const cb = typeof predicate === 'function' ? predicate : (o) => o[predicate];

  const pickedObjects = arr
    .filter((item) => item)
    .reduce((acc, item) => {
      const key = cb(item);

      if (!key) {
        return [...acc, item];
      }

      return acc[key] ? acc : [...acc, item];
    }, [] as T[]);

  return pickedObjects;
};

export const isGlobalProduct = (
  product: any | undefined | null,
): product is GlobalProduct => {
  return (
    isArray(product.variants) &&
    product.variants?.length > 0 &&
    isArray(product.variantsOptions) &&
    product.variantsOptions?.length > 0
  );
};

export const isGlobalProductList = (
  productList: unknown,
): productList is GlobalProduct[] => {
  if (isArray(productList) && productList.length > 0) {
    return productList.every(isGlobalProduct);
  }

  return false;
};

export const splitProductInVariants = (
  bundleProducts: GlobalProduct[],
  productsInBundle: Record<string, number>,
): GlobalProduct[] => {
  return bundleProducts.reduce(
    (acc: GlobalProduct[], product: GlobalProduct) => {
      let isRepeated = false;

      for (const variant of product.variants) {
        if (Object.keys(productsInBundle).includes(`${variant.id}`)) {
          if (isRepeated) return [...acc, ...splitProduct(product)];
          isRepeated = true;
        }
      }

      return [...acc, product];
    },
    [] as GlobalProduct[],
  );
};

const splitProduct = (product: GlobalProduct): GlobalProduct[] => {
  return product.variants.map((v) =>
    formatGlobalVariantInGlobalProduct(v, product),
  );
};

export const notEmpty = <Value>(value: Value): value is NonNullable<Value> => {
  return value !== null && value !== undefined;
};

export const isOneOf = <T>(value: T, values: T[]): boolean => {
  return values.includes(value);
};

export const getFirstAvailableVariant = (
  variants: GlobalVariant[],
): GlobalVariant | undefined => {
  return variants.find((variant) => variant.available);
};

export const isProductAvailable = (
  product: GlobalProduct | GlobalVariant,
  shopConfiguration: ShopConfiguration,
  ignoreBaBOutOfStock = false,
): boolean => {
  const isIgnoreOutOfStockEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.LAUNCH_BAB_IGNORE_OUT_OF_STOCK,
  );

  const inventory = product.inventory;
  let isAvailable = true;

  if (isIgnoreOutOfStockEnabled && ignoreBaBOutOfStock && inventory) {
    const noInventory =
      inventory.inventoryPolicy === 'continue' &&
      inventory.inventoryManagement === 'shopify' &&
      inventory.inventoryQuantity <= 0;

    isAvailable = product.available && !noInventory;
  } else {
    isAvailable =
      product.available || shopConfiguration?.ShopOversell === 'ALLOW';
  }

  return isAvailable;
};
